import { useEffect, useRef, useState } from 'react';
import { IconAlertCircle, IconChevronDown, IconHistory, IconTrash, IconX } from '@tabler/icons-react';
import Hint from '../../../../../components/common/Hint';

export const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors placeholder:text-[#8A827A] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-400';

export function chipBtnClass(active) {
    return `rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${
        active
            ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300'
            : 'border-[#ECE8E4] text-[#6B635C] hover:border-amber-400/40 dark:border-zinc-700 dark:text-zinc-300'
    }`;
}

export function tabBtnClass(active) {
    return `rounded-lg border px-2.5 py-2 text-[12px] font-semibold transition-colors sm:px-3 sm:text-[12.5px] ${
        active
            ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300'
            : 'border-[#ECE8E4] text-[#6B635C] hover:border-amber-400/40 dark:border-zinc-700 dark:text-zinc-300'
    }`;
}

/**
 * Content-type pill. The unpicked types fade back so the active one reads at a
 * glance in a row of eighteen; hovering brings a chip back to full strength.
 */
export function typeChipClass(active) {
    return `rounded-xl border px-2.5 text-[13.5px] font-semibold transition-all ${
        active
            ? 'border-primary bg-primary-50 text-primary-700 shadow-[0_1px_4px_rgba(230,138,0,0.15)] dark:border-primary dark:bg-primary-950/30 dark:text-primary-400'
            : 'border-[#ECE8E4] bg-white text-[#3D3833] opacity-55 hover:border-primary/40 hover:opacity-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
    }`;
}

/** Square thumbnail button used by the shape, preset and palette grids. */
export function swatchBtnClass(active) {
    return `flex items-center justify-center rounded-[11px] bg-white p-1 text-[#1C1815] transition-all dark:bg-zinc-900 dark:text-zinc-100 ${
        active
            ? 'border-2 border-primary ring-[3px] ring-primary/15'
            : 'border border-[#ECE8E4] hover:border-primary/40 dark:border-zinc-700'
    }`;
}

/** iOS-style segmented control: one pill slides inside a sunken track. */
export function SegRow({ options, value, onChange, ariaLabel }) {
    return (
        <div role="group" aria-label={ariaLabel} className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
            {options.map((opt) => {
                const active = opt.value === value;
                return (
                    <Hint label={opt.title} key={String(opt.value)}>
                    <button
                        type="button"
                        onClick={() => onChange(opt.value)}
                        aria-pressed={active}
                        // An option can render as an icon and still say what it
                        // is: the label for a screen reader, the hint on hover.
                        aria-label={opt.title}
                        className={`flex flex-1 items-center justify-center whitespace-nowrap rounded-[9px] px-2.5 py-2 text-[12.5px] font-semibold transition-colors ${
                            active
                                ? 'bg-white text-[#1C1815] shadow-[0_1px_3px_rgba(28,24,21,0.10)] dark:bg-zinc-900 dark:text-zinc-100'
                                : 'text-[#6B635C] hover:text-[#3D3833] dark:text-zinc-400'
                        }`}
                    >
                        {opt.label}
                    </button>
                    </Hint>
                );
            })}
        </div>
    );
}

/** Numbered step card — the always-open cards at the top of the left column. */
export function StepCard({ step, title, hint, children }) {
    return (
        <div className="rounded-[20px] border border-[#ECE8E4] bg-white p-[18px] shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
            <div className="mb-3 flex items-center gap-2.5">
                <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-primary-50 text-[12px] font-bold text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
                    {step}
                </span>
                <span className="text-[14.5px] font-bold text-[#1C1815] dark:text-zinc-100">{title}</span>
                {hint ? <span className="ml-auto text-[12px] text-[#6B635B] dark:text-zinc-400">{hint}</span> : null}
            </div>
            {children}
        </div>
    );
}

/** Small caption above a grid of swatches inside an open section. */
export function GroupLabel({ children, right }) {
    return (
        <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">{children}</span>
            {right ? <span className="font-mono text-[11.5px] text-[#6B635B] dark:text-zinc-400">{right}</span> : null}
        </div>
    );
}

/**
 * Text input that offers what was typed into this field before.
 *
 * Deliberately not a `<datalist>`: the browser renders that as an unstyled OS
 * menu that ignores the surrounding design. This is a small combobox instead —
 * same list, our own styling, arrow-key and Escape support.
 */
export function SuggestInput({
    suggestKey,
    suggestions = [],
    value,
    onChange,
    onRemove,
    removeLabel,
    onClearAll,
    clearAllLabel,
    ...rest
}) {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapRef = useRef(null);

    const typed = String(value || '').trim().toLowerCase();
    const matches = suggestions
        .filter((item) => item.toLowerCase() !== typed)
        .filter((item) => !typed || item.toLowerCase().includes(typed))
        .slice(0, 6);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const choose = (item) => {
        onChange({ target: { value: item } });
        setOpen(false);
        setActiveIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!matches.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => (i + 1) % matches.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? matches.length - 1 : i - 1));
        } else if (e.key === 'Enter' && open && activeIndex >= 0) {
            e.preventDefault();
            choose(matches[activeIndex]);
        } else if (e.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
        }
    };

    const showList = open && matches.length > 0;

    return (
        <span ref={wrapRef} className="relative block">
            <input
                {...rest}
                value={value}
                onChange={(e) => { onChange(e); setOpen(true); setActiveIndex(-1); }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                name={`qr-${suggestKey}`}
                autoComplete="off"
                role="combobox"
                aria-expanded={showList}
                aria-autocomplete="list"
            />
            {showList ? (
                <span className="absolute left-0 right-0 top-full z-30 mt-1.5 block overflow-hidden rounded-xl border border-[#ECE8E4] bg-white py-1 shadow-[0_10px_28px_rgba(28,24,21,0.12)] dark:border-zinc-700 dark:bg-zinc-900">
                    {matches.map((item, index) => (
                        <span
                            key={item}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`group flex items-center transition-colors ${
                                index === activeIndex
                                    ? 'bg-primary-50 dark:bg-primary-950/30'
                                    : ''
                            }`}
                        >
                            <button
                                type="button"
                                // mousedown fires before blur, so the click still lands.
                                onMouseDown={(e) => { e.preventDefault(); choose(item); }}
                                className={`flex min-w-0 flex-1 items-center gap-2 py-2 pl-3 text-left text-[13px] ${
                                    index === activeIndex
                                        ? 'text-primary-700 dark:text-primary-400'
                                        : 'text-[#3D3833] dark:text-zinc-300'
                                }`}
                            >
                                <IconHistory size={14} stroke={1.8} className="flex-none text-[#A8A099]" />
                                <span className="truncate">{item}</span>
                            </button>
                            {onRemove ? (
                                <Hint label={removeLabel}>
                                <button
                                    type="button"
                                    aria-label={removeLabel}
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(item); }}
                                    className="mr-1.5 flex h-6 w-6 flex-none items-center justify-center rounded-md text-[#A8A099] opacity-0 transition-all hover:bg-white hover:text-[#D33A2C] focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-zinc-800"
                                >
                                    <IconX size={13} stroke={2} />
                                </button>
                                </Hint>
                            ) : null}
                        </span>
                    ))}
                    {onClearAll ? (
                        <span className="mt-1 block border-t border-[#F2EFEC] pt-1 dark:border-zinc-800">
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); onClearAll(); setOpen(false); }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] font-semibold text-[#6B635B] transition-colors hover:text-[#D33A2C]"
                            >
                                <IconTrash size={13} stroke={1.8} className="flex-none" />
                                {clearAllLabel}
                            </button>
                        </span>
                    ) : null}
                </span>
            ) : null}
        </span>
    );
}

/** `error` turns the hint red and outlines the control, so bad input is obvious. */
export function Field({ label, hint, error, children }) {
    return (
        <label className="block space-y-1.5">
            <span className="flex items-baseline justify-between gap-2">
                <span className="text-[13.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{label}</span>
                {hint && !error ? <span className="text-[11.5px] font-medium text-[#6B635B] dark:text-zinc-400">{hint}</span> : null}
            </span>
            <span className={error ? 'block rounded-lg ring-1 ring-[#D33A2C]' : 'block'}>
                {children}
            </span>
            {error ? (
                <span className="flex items-start gap-1 text-[11.5px] font-medium text-[#D33A2C]">
                    <IconAlertCircle size={13} className="mt-px flex-none" />
                    {error}
                </span>
            ) : null}
        </label>
    );
}

export function ColorField({ label, value, onChange }) {
    return (
        <Field label={label}>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-9 w-11 flex-none cursor-pointer rounded-lg border border-[#ECE8E4] bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${inputClass} font-mono text-[12px]`}
                />
            </div>
        </Field>
    );
}

export function SliderField({ label, value, min, max, step = 1, suffix = '', display, onChange }) {
    return (
        <Field label={label} hint={`${display ?? value}${suffix}`}>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[#ECE8E4] accent-amber-500 dark:bg-zinc-700"
            />
        </Field>
    );
}

export function ToggleRow({ label, checked, onChange, hint }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            role="switch"
            aria-checked={checked}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-left transition-colors hover:border-amber-400/50 dark:border-zinc-700 dark:bg-zinc-900"
        >
            <span className="min-w-0">
                <span className="block text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{label}</span>
                {hint ? <span className="block text-[11px] text-[#6B635B] dark:text-zinc-400">{hint}</span> : null}
            </span>
            <span className={`relative h-5 w-9 flex-none rounded-full transition-colors ${checked ? 'bg-amber-500' : 'bg-[#DCD6D0] dark:bg-zinc-600'}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
        </button>
    );
}

/**
 * Collapsible settings card. Fully controlled so the sidebar can behave as one
 * accordion — only the section the user is working in stays open.
 * Deliberately not `overflow-hidden`: dropdown menus inside must be able to
 * spill past the card edge.
 */
export function Section({ title, badge, summary, open, onToggle, children }) {
    return (
        <div className="rounded-[20px] border border-[#ECE8E4] bg-white shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="flex w-full items-center gap-2.5 px-[18px] py-4 text-left"
            >
                <span className="flex min-w-0 items-center gap-2">
                    <span className="text-[14.5px] font-bold text-[#1C1815] dark:text-zinc-100">{title}</span>
                    {badge ? (
                        <span className="rounded-full bg-[#F7F5F3] px-2 py-0.5 text-[10.5px] font-semibold text-[#6B635C] dark:bg-zinc-800 dark:text-zinc-400">
                            {badge}
                        </span>
                    ) : null}
                </span>
                {summary ? (
                    <span className="ml-auto flex min-w-0 items-center gap-1.5 truncate text-[12px] text-[#6B635B] dark:text-zinc-400">
                        {summary}
                    </span>
                ) : null}
                <IconChevronDown
                    size={17}
                    className={`flex-none text-[#A8A099] transition-transform ${summary ? '' : 'ml-auto'} ${open ? 'rotate-180' : ''}`}
                />
            </button>
            {open ? <div className="space-y-4 px-[18px] pb-[18px]">{children}</div> : null}
        </div>
    );
}
